/**
 * Pure decisions for the soil session detail screen (U3).
 *
 * No React, no network, no i18n — the page above this file is presentation.
 * This is the same split that carried the U2 grouping builder: the logic worth
 * testing exhaustively lives here and is covered without a DOM.
 *
 * NOTE ON WHAT IS DELIBERATELY ABSENT: there is no actual-kg field and no
 * variance. soilSignoffService.js:205 sets actualKg = plannedKg — no operator
 * entry exists — so a variance column would always read zero and an "Actual"
 * column would misrepresent a plan as an observation (spec §2.7).
 */
import { SoilSession, SoilSessionGroup } from '../../services/soilSessionService';
import {
  WaitingTone, WAITING_DANGER_DAYS, waitingTone, waitingDaysSince,
} from './soilSignOff';

export type GroupAction = 'record' | 'signoff' | 'none';

export interface DetailRow {
  groupId: string;
  label: string;
  orchardNames: string[];
  cultivars: string[];
  treeCount: number;
  areaHa: number;
  plannedKg: number;
  startedAt: string | null;
  completedAt: string | null;
  ecReading: number | null;
  signatureName: string | null;
  operators: string[];
  status: string;
  action: GroupAction;
  queued: boolean;
  lateSignoff: boolean;
  waitingDays: number | null;
  /**
   * Tone for the waiting pill, from the SAME `waitingTone` the sign-off queue
   * uses — so a 4-day-old group is amber on both screens. null exactly when
   * waitingDays is null (ageing does not apply).
   */
  tone: WaitingTone | null;
  /** #482: 'backcapture' drives the provenance pill. Null before any capture. */
  captureChannel: 'field' | 'backcapture' | null;
}

export interface DetailTotals {
  treeCount: number;
  areaHa: number;
  plannedKg: number;
  orchardCount: number;
}

/**
 * Days a completed group may wait before the row is flagged red.
 *
 * Deliberately an alias, not a literal: the red row-stripe here and the danger
 * pill in the sign-off queue are the same boundary, so there is exactly ONE
 * definition of it (./soilSignOff.WAITING_DANGER_DAYS). Kept exported because
 * soilSessionDetail.test.ts derives its fixtures from it.
 */
export const LATE_SIGNOFF_DAYS = WAITING_DANGER_DAYS;

/** The screen must never offer a transition the backend would reject. */
export function actionForStatus(status: string): GroupAction {
  if (status === 'not_started' || status === 'in_progress') return 'record';
  if (status === 'complete') return 'signoff';
  return 'none';
}

/**
 * Row modifiers carry state that is NOT a status: blue for an offline-pending
 * transition, red for a group waiting too long to be signed off. Red wins —
 * matching the visual outcome RowCard produces between its `queued` and
 * `lateSignoff` modifiers (RowCard applies both CSS classes at once and lets
 * cascade order decide the winner; this function resolves the same
 * precedence in JS and returns a single class), kept the same so the two
 * surfaces cannot disagree on which colour a row shows.
 */
export function rowModifierClass(row: Pick<DetailRow, 'queued' | 'lateSignoff'>): string {
  if (row.lateSignoff) return 'ntr-det-row--late';
  if (row.queued) return 'ntr-det-row--queued';
  return '';
}

export function buildDetailRows(
  groups: SoilSessionGroup[],
  opts: { pendingGroupIds: string[]; now: Date },
): DetailRow[] {
  const pending = new Set(opts.pendingGroupIds);
  return (groups || []).map((g) => {
    // Ageing applies only while the group is still WAITING. Once signed off the
    // wait is over, so an old completedAt must not keep escalating the row —
    // that decision is this screen's, hence the null, and it is made HERE
    // rather than inside waitingDaysSince (which cannot tell "not ageing yet"
    // from "aged 0 days" and must return a number for the queue).
    // The day count itself and the tone it earns come from soilSignOff, the
    // single owner of both — this file no longer restates either.
    const waiting = g.status === 'complete' && g.completedAt
      ? waitingDaysSince(g.completedAt, opts.now)
      : null;
    return {
      groupId: g.groupId,
      label: g.label || g.groupId,
      orchardNames: g.orchardNames || [],
      cultivars: g.cultivars || [],
      treeCount: g.treeCount ?? 0,
      areaHa: g.areaHa ?? 0,
      plannedKg: g.plannedKg ?? 0,
      startedAt: g.startedAt ?? null,
      completedAt: g.completedAt ?? null,
      ecReading: g.ecReading ?? null,
      signatureName: g.signatureName ?? null,
      operators: (g.assignedOperators || []).map((o) => o.name),
      status: g.status,
      action: actionForStatus(g.status),
      queued: pending.has(g.groupId),
      lateSignoff: waiting !== null && waiting >= LATE_SIGNOFF_DAYS,
      waitingDays: waiting,
      tone: waiting === null ? null : waitingTone(waiting),
      captureChannel: g.captureChannel ?? null,
    };
  });
}

export function detailTotals(rows: DetailRow[]): DetailTotals {
  return {
    treeCount: rows.reduce((s, r) => s + r.treeCount, 0),
    areaHa: rows.reduce((s, r) => s + r.areaHa, 0),
    plannedKg: rows.reduce((s, r) => s + r.plannedKg, 0),
    orchardCount: rows.reduce((s, r) => s + r.orchardNames.length, 0),
  };
}

/**
 * Orchards in the session but in no group. They are silently excluded from the
 * instruction, so the screen names them rather than letting them vanish.
 */
export function ungroupedOrchards(session: Pick<SoilSession, 'orchards' | 'groups'>): string[] {
  const assigned = new Set((session.groups || []).flatMap((g) => g.orchardNames || []));
  return (session.orchards || []).filter((o) => !assigned.has(o));
}
